import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { mx_noise_float, positionLocal, vec3 } from 'three/tsl';
import * as THREE from 'three/webgpu';
import { HERO_EYE, type HeroModel } from '../../engine/character/HeroModel';
import { placeBone, solveLimb } from '../../engine/character/limbIK';
import { bakeMeshes } from '../../engine/render/bakeMeshes';
import type { Attack } from './Attack';
import { buildWeaponModel, offHand, weaponMaterials } from './weaponModels';
import { type HeldPose, handAlong, poseQuaternion, WeaponMotion } from './weaponMotion';
import type { Weapon } from './weapons';

type N = any;

/** First-person shoulders (view space, m: behind and below the eye) and the arm's bones. */
const SHOULDER_R = new THREE.Vector3(0.19, -0.23, 0.12);
const SHOULDER_L = new THREE.Vector3(-0.19, -0.23, 0.12);
const UPPER = 0.3;
const FORE = 0.28;
/** Elbows bend down and out. */
const POLE_R = new THREE.Vector3(0.6, -0.9, 0.3);
const POLE_L = new THREE.Vector3(-0.6, -0.9, 0.3);
/** Where the free hand rests when the weapon takes one (low by the hip, out of sight). */
const FREE_HAND = new THREE.Vector3(-0.24, -0.62, -0.12);

const _pitch = new THREE.Quaternion();
const _X = new THREE.Vector3(1, 0, 0);
const _main = new THREE.Vector3();
const _off = new THREE.Vector3();
/** In third person the hero's grip stays at least this far in front of its eye (m): clear of its chest. */
const HERO_REACH = 0.28;
const _a = new THREE.Vector3();
const _e = new THREE.Vector3();
const _h = new THREE.Vector3();
const _s = new THREE.Vector3();

/**
 * A gloved fist closed round a grip along its +Y: four fingers wrapped round the handle, the
 * hand over the side they leave open, and the thumb across them.
 */
function fist(mat: THREE.Material): THREE.Group {
  const g = new THREE.Group();
  const add = (geo: THREE.BufferGeometry, x: number, y: number, z: number) => {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z);
    m.frustumCulled = false;
    g.add(m);
    return m;
  };
  // Fingers: rings round the grip, open on the palm's side (between −Z and +X), the little
  // finger smallest.
  const fingers: [number, number][] = [
    [0.031, 0.0125],
    [0.01, 0.013],
    [-0.012, 0.0125],
    [-0.033, 0.011],
  ];
  for (const [y, r] of fingers) {
    const ring = new THREE.TorusGeometry(0.027, r, 6, 12, Math.PI * 1.45);
    ring.rotateX(Math.PI / 2);
    add(ring, 0, y, 0);
  }
  // The hand itself over the open side (palm against the grip, back outward), and the thumb
  // across the top of the fingers.
  const hand = add(new RoundedBoxGeometry(0.045, 0.092, 0.07, 2, 0.016), 0.034, -0.002, -0.034);
  hand.rotation.y = Math.PI / 4;
  const thumb = add(new THREE.CapsuleGeometry(0.0125, 0.032, 3, 6), 0.03, 0.044, 0.0);
  thumb.rotation.set(Math.PI / 2, 0, 0.2);
  return g;
}

/** A bone: a unit-long tapered piece along +Y (scaled to length by placeBone). */
function boneGeometry(r0: number, r1: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r1, r0, 1, 10, 1, true);
  g.translate(0, 0.5, 0);
  return g;
}

/**
 * The traveller's arms in first person: sleeves, gloved hands on the weapon's grip (both on a
 * two-handed weapon), elbows bent down and out. They reach for wherever the weapon is.
 */
class Arms {
  readonly group = new THREE.Group();
  private readonly upper: THREE.Mesh[] = [];
  private readonly fore: THREE.Mesh[] = [];
  private readonly joints: THREE.Mesh[] = [];
  private readonly hands: THREE.Object3D[] = [];
  readonly materials: THREE.MeshStandardNodeMaterial[];

  constructor() {
    const sleeve = new THREE.MeshStandardNodeMaterial({ roughness: 0.92 });
    // Woven cloth: a fine weave and folds.
    const weave = mx_noise_float((positionLocal as N).mul(vec3(260, 90, 260)));
    sleeve.colorNode = vec3(0.12, 0.27, 0.28).mul(weave.mul(0.08).add(1));
    const glove = new THREE.MeshStandardNodeMaterial({ roughness: 0.7 });
    const wear = mx_noise_float((positionLocal as N).mul(70));
    glove.colorNode = vec3(0.16, 0.1, 0.06).mul(wear.mul(0.15).add(1));
    this.materials = [sleeve, glove];
    for (let i = 0; i < 2; i++) {
      const up = new THREE.Mesh(boneGeometry(0.052, 0.046), sleeve);
      const fo = new THREE.Mesh(boneGeometry(0.045, 0.036), sleeve);
      // A leather cuff over the wrist end of the sleeve.
      const cuff = new THREE.Mesh(new THREE.CylinderGeometry(0.044, 0.04, 0.09, 10, 1, true), glove);
      cuff.position.y = 0.92;
      cuff.scale.set(1, 1 / 0.28, 1);
      fo.add(cuff);
      const elbow = new THREE.Mesh(new THREE.SphereGeometry(0.047, 10, 8), sleeve);
      const hand = fist(glove);
      for (const m of [up, fo, elbow, cuff]) m.frustumCulled = false;
      for (const m of [up, fo, elbow, hand]) m.userData.moves = true;
      this.group.add(up, fo, elbow, hand);
      this.upper.push(up);
      this.fore.push(fo);
      this.joints.push(elbow);
      this.hands.push(hand);
    }
    // Each fist in one mesh.
    bakeMeshes(this.group);
  }

  /** Reach for the grip (and the off hand's place, or let that hand rest), the hands turned like the weapon. */
  pose(pose: HeldPose, weaponQ: THREE.Quaternion, off: THREE.Vector3 | null): void {
    this.reach(0, SHOULDER_R, pose.grip, POLE_R, weaponQ);
    if (off) this.reach(1, SHOULDER_L, off, POLE_L, weaponQ);
    else this.reach(1, SHOULDER_L, FREE_HAND, POLE_L, null);
  }

  private reach(
    i: number,
    shoulder: THREE.Vector3,
    hand: THREE.Vector3,
    pole: THREE.Vector3,
    q: THREE.Quaternion | null,
  ): void {
    _s.copy(shoulder);
    // The fist sits just behind the grip's line (the handle runs through the fingers).
    _h.copy(hand);
    solveLimb(_s, _h, UPPER, FORE, _a.copy(_s).add(pole), _e);
    placeBone(this.upper[i], _s, _e);
    placeBone(this.fore[i], _e, _h);
    this.joints[i].position.copy(_e);
    const fist = this.hands[i];
    fist.position.copy(_h);
    if (q) fist.quaternion.copy(q);
    else fist.quaternion.setFromUnitVectors(_a.set(0, 1, 0), _e.subVectors(_h, _e).normalize());
  }
}

/**
 * The weapon in first person, held in the traveller's hands and moved by WeaponMotion: at rest
 * close and below the aim, swung toward where a press began, struck out the way the aim moves.
 */
export class WeaponView {
  /** Placed at the eye (the render origin), turned with the view. */
  readonly group = new THREE.Group();
  readonly motion = new WeaponMotion();
  private readonly models = new Map<string, THREE.Group>();
  private readonly arms = new Arms();
  private readonly materials = weaponMaterials();
  private current = '';
  private readonly weaponQ = new THREE.Quaternion();
  private readonly off = new THREE.Vector3();
  /** The hero that holds the same weapons in third person, and its copies of them. */
  private hero: HeroModel | null = null;
  private readonly heroModels = new Map<string, THREE.Group>();

  constructor(private readonly weapons: Weapon[]) {
    this.group.name = 'weapon view';
    for (const w of weapons) {
      const m = buildWeaponModel(w, this.materials);
      m.visible = false;
      this.group.add(m);
      this.models.set(w.id, m);
    }
    this.group.add(this.arms.group);
  }

  /**
   * Give the hero copies of the weapons to hold in third person. Both views are lit by the
   * hero's own lights (see HeroModel): a world's lantern hangs right by the hands and burned
   * them white, and the hero's is held to a sane brightness that close.
   */
  holdBy(hero: HeroModel): void {
    this.hero = hero;
    for (const m of [...Object.values(this.materials), ...this.arms.materials])
      m.lightsNode = hero.lightsNode;
    for (const w of this.weapons) {
      const m = buildWeaponModel(w, this.materials);
      m.visible = false;
      hero.held.add(m);
      this.heroModels.set(w.id, m);
    }
    this.current = '';
  }

  /** This frame's pose (view space), for holding the same in third person. */
  get pose(): HeldPose {
    return this.motion.pose;
  }

  /**
   * Per frame: move the weapon for `attack` (and how the aim moved), place it at the eye, and
   * put the hero's copy in its hands (`pitch`: the view's, which the hero's body doesn't follow).
   * While `warm` (the game booting, behind its boot screen) every weapon shows, so all their
   * shaders compile then rather than when one is first picked.
   */
  frame(
    dt: number,
    attack: Attack,
    viewQ: THREE.Quaternion,
    visible: boolean,
    pitch: number,
    warm = false,
  ): void {
    const w = attack.weapon;
    if (warm) {
      for (const m of this.models.values()) m.visible = true;
      for (const m of this.heroModels.values()) m.visible = true;
      this.current = '';
    } else if (w.id !== this.current) {
      for (const [id, m] of this.models) m.visible = id === w.id;
      for (const [id, m] of this.heroModels) m.visible = id === w.id;
      this.current = w.id;
    }
    const pose = this.motion.update(dt, attack, viewQ);
    this.group.visible = visible || warm;
    this.group.position.set(0, 0, 0);
    this.group.quaternion.copy(viewQ);
    poseQuaternion(pose, this.weaponQ);
    for (const [id, model] of this.models) {
      if (id !== w.id && !warm) continue;
      model.position.copy(pose.grip);
      model.quaternion.copy(this.weaponQ);
    }
    const along = offHand(w);
    this.arms.pose(pose, this.weaponQ, along === null ? null : handAlong(pose, along, this.off));
    if (this.hero) this.holdInThirdPerson(this.hero, w, pose, pitch, along);
  }

  /**
   * The hero's copy: the same pose, from its eye and turned by the view's pitch (its body only
   * turns with the yaw), kept in front of its chest when it looks down.
   */
  private holdInThirdPerson(
    hero: HeroModel,
    w: Weapon,
    pose: HeldPose,
    pitch: number,
    along: number | null,
  ): void {
    const model = this.heroModels.get(w.id)!;
    _pitch.setFromAxisAngle(_X, pitch);
    _main.copy(pose.grip).applyQuaternion(_pitch);
    _main.z = Math.min(_main.z, -HERO_REACH);
    _main.y += HERO_EYE;
    model.position.copy(_main);
    model.quaternion.copy(_pitch).multiply(this.weaponQ);
    if (along !== null) _off.set(0, along, 0).applyQuaternion(model.quaternion).add(_main);
    hero.setHands(_main, along === null ? null : _off, model.quaternion);
  }
}
